import { pipe } from "fp-ts/lib/function";
import * as O from "fp-ts/lib/Option";
import React from "react";

export function formatPrice(price: number, currency: "EUR"): string {
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
}

export function formatArea(area: number): string {
  return `${formatNumber(area, { maximumFractionDigits: 2 })} m²`;
}

export function formatAreaTransformer(
  area: number,
  tx: (x: string) => React.ReactNode,
) {
  const a = formatNumber(area, { maximumFractionDigits: 0 });
  return <React.Fragment>{tx(a)} m²</React.Fragment>;
}

export function formatAreaTransformerS(
  area: number,
  tx: (x: string) => string,
) {
  const a = formatNumber(area, { maximumFractionDigits: 0 });
  return `${tx(a)} m²`;
}

export function formatPricePerArea(price: number, currency: "EUR"): string {
  return formatPrice(price, currency) + " / m²";
}

export function formatPricePerMonth(price: number, currency: "EUR"): string {
  return formatPrice(price, currency) + " / mese";
}

export function formatNumber(
  n: number,
  opts?: { minimumFractionDigits?: number; maximumFractionDigits?: number },
): string {
  return new Intl.NumberFormat("it-IT", {
    minimumFractionDigits: pipe(
      O.fromNullable(opts),
      O.mapNullable(x => x.minimumFractionDigits),
      O.getOrElse(() => 0),
    ),
    maximumFractionDigits: pipe(
      O.fromNullable(opts),
      O.mapNullable(x => x.maximumFractionDigits),
      O.getOrElse(() => 0),
    ),
  }).format(n);
}

export function formatExponent(
  v: NonNullable<React.ReactNode>,
  exp: NonNullable<React.ReactNode>,
): React.ReactElement {
  return React.createElement("span", {}, [
    v,
    React.createElement("sup", { key: "sup" }, exp),
  ]);
}

export function formatPercent(n: number): React.ReactElement {
  return formatExponent(n, "%");
}

export function formatDate(d: Date): string {
  return new Intl.DateTimeFormat("it-IT", {
    day: "numeric",
    month: "short",
    year: d.getUTCFullYear() === new Date().getUTCFullYear()
      ? undefined
      : "numeric",
  }).format(d);
}

export function formatDateLong(d: Date): string {
  return new Intl.DateTimeFormat("it-IT", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(d);
}
